import PropTypes from "prop-types";
import { Chip, FormControl, MenuItem, Select } from "@mui/material";

export const MARKETING_CALL_STATUS_OPTIONS = [
  {
    value: "not_called",
    label: "Not called",
    color: "#9DB4CF",
    background: "rgba(143, 167, 196, 0.1)",
    border: "rgba(143, 167, 196, 0.28)",
  },
  {
    value: "picked",
    label: "Picked",
    color: "#55D68E",
    background: "rgba(40, 183, 111, 0.12)",
    border: "rgba(85, 214, 142, 0.34)",
  },
  {
    value: "not_picked",
    label: "Not picked",
    color: "#F5A451",
    background: "rgba(242, 117, 0, 0.12)",
    border: "rgba(245, 164, 81, 0.34)",
  },
];

const getOption = (status) =>
  MARKETING_CALL_STATUS_OPTIONS.find((option) => option.value === status) ||
  MARKETING_CALL_STATUS_OPTIONS[0];

export function MarketingCallStatusChip({ status }) {
  const option = getOption(status);
  return (
    <Chip
      size="small"
      label={option.label}
      sx={{
        color: option.color,
        backgroundColor: option.background,
        border: `1px solid ${option.border}`,
        fontWeight: 800,
        letterSpacing: "0.01em",
      }}
    />
  );
}

MarketingCallStatusChip.propTypes = {
  status: PropTypes.string,
};

MarketingCallStatusChip.defaultProps = {
  status: "not_called",
};

export function MarketingCallStatusSelect({ status, disabled, onChange, ariaLabel }) {
  const selectedOption = getOption(status);

  return (
    <FormControl size="small" sx={{ minWidth: 132 }}>
      <Select
        value={selectedOption.value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ "aria-label": ariaLabel }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.75,
              color: "#EDF4FF",
              borderRadius: "12px",
              border: "1px solid rgba(126,170,220,0.18)",
              background: "linear-gradient(180deg, #0D1D2F 0%, #08131F 100%)",
              boxShadow: "0 18px 44px rgba(0,0,0,0.42)",
              "& .MuiMenuItem-root": {
                color: "#D7E5F5",
                fontSize: "0.875rem",
                fontWeight: 600,
                "&:hover": { backgroundColor: "rgba(85,214,142,0.09)" },
                "&.Mui-selected": {
                  color: "#FFFFFF",
                  backgroundColor: "rgba(40,183,111,0.16)",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(40,183,111,0.22)",
                },
              },
            },
          },
        }}
        sx={{
          minHeight: 38,
          color: `${selectedOption.color} !important`,
          borderRadius: "10px",
          backgroundColor: selectedOption.background,
          fontWeight: 800,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: selectedOption.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: selectedOption.color,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: selectedOption.color,
          },
          "& .MuiSelect-select": {
            minHeight: "auto !important",
            py: "9px !important",
            color: `${selectedOption.color} !important`,
            WebkitTextFillColor: `${selectedOption.color} !important`,
          },
          "& .MuiSvgIcon-root": { color: selectedOption.color },
          "&.Mui-disabled": { opacity: 0.65 },
        }}
      >
        {MARKETING_CALL_STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

MarketingCallStatusSelect.propTypes = {
  status: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

MarketingCallStatusSelect.defaultProps = {
  status: "not_called",
  disabled: false,
  ariaLabel: "Marketing call status",
};
